import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MobileStepper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme
} from "@mui/material";
import { DateTime } from "luxon";
import { Close, KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useStore } from "react-redux";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { BookingType, Frequence } from "../../Booking-Form/typings/booking-inputs";
import {
  defaultRecurrenceWeekdays,
  type Weekdays
} from "../../Booking-Form/typings/shared/weekdays";
import {
  clearFloors,
  clearInputs,
  selectResourceBooking,
  selectVehicleBooking,
  setFloors,
  setInputs
} from "../../Booking-Form/slices/booking.slice";
import { fetchBookingFloors } from "../../Booking-Form/thunks/booking.thunks";
import { getBookingType } from "../../../components/BookingForm/utils/booking.utils";
import FormConfirm from "../../../components/BookingForm/form-confirm.component";
import { useRemoteResourceScheduleByBookingObjectMutation } from "../../../hooks/Remote/Schedule/useRemoteResourceScheduleByBookingObjectMutation";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import {
  BookableBookingObjectDto,
  ResourceInactivityResponseDto
} from "../typings/resource-calendar.types";
import { createStubFleetVehicleForResourceBooking } from "../functions/createStubFleetVehicleForResourceBooking";
import { fetchResourceInfoForConfirm } from "../functions/fetchResourceInfoForConfirm";
import { resourceInfoFromPrefetchOrStub } from "../functions/resourceInfoFromPrefetchOrStub";
import { vehicleInfoFromPrefetchOrStub } from "../functions/vehicleInfoFromPrefetchOrStub";
import type { BookingResourceInfo } from "../typings/booking-resource-info.types";
import { buildResourceCalendarByObjectRequest } from "../utils/resource-booking-schedule.utils";
import { BookingVehicleInfo } from "../../../components/FleetManagement/types/FleetManagement-row.types";
import { mapVehicleInventoryToFleetManagementRow } from "../../../components/FleetManagement/helpers/mapVehicleInventoryToFleetManagementRow.function";
import { getFleetVehicleInfo } from "../../../utils/axios-requests";
import {
  resolveCurrentFloorIndexForResourceBookConfirm,
  resolveResourceBookLocationForConfirm
} from "../functions/scheduleResourceBookingConfirmHydrate";
import { ScheduleResourceBookConferenceStep } from "./ScheduleResourceBookConferenceStep.partial";
import { ScheduleResourceBookDateStep } from "./ScheduleResourceBookDateStep.partial";
import {
  listIsoDaysInclusive,
  resourceBookTimePickerValidRanges
} from "../functions/buildResourceBookDayAvailabilitySegments";
import {
  isDialogDateRangeWithinCalendarPrefetchRange,
  selectSchedulesOverlappingDialogDateRange
} from "../functions/resourceBookDialogSchedulesFromPrefetch";
import { tryReadVehicleInfoPrefetchCache } from "../functions/tryReadVehicleInfoPrefetchCache";
import { initialChosenTimesForResourceBookDialog } from "../../../components/BookingForm/functions/date-time.functions";
import { defaultMonthRange } from "./defaultMonthRange";

export type ScheduleResourceBookCalendarPrefetch = {
  inactivity?: ResourceInactivityResponseDto;
  rangeEndIso: string;
  rangeStartIso: string;
  schedules: BookingScheduleInterface[];
};

type P = {
  /** When the main calendar already loaded this resource for a range that covers the dialog dates, skip `by-booking-object`. */
  calendarPrefetch?: ScheduleResourceBookCalendarPrefetch | null;
  initialPageRange?: { end: string; start: string } | null;
  onClose: () => void;
  open: boolean;
  row: BookableBookingObjectDto | null;
};

const recurrenceWeekdaysAllTrue: Weekdays = {
  Monday: true,
  Tuesday: true,
  Wednesday: true,
  Thursday: true,
  Friday: true,
  Saturday: true,
  Sunday: true
};

export function deriveResourceRecurrenceState(
  bookingFrom: string,
  bookingTo: string,
  bookingType: BookingType | undefined
) {
  const specificDays = listIsoDaysInclusive(bookingFrom, bookingTo);
  const hasDateInterval = specificDays.length > 1;
  const isVehicleMultiDayRange = bookingType === BookingType.VEHICLE && hasDateInterval;
  const frequenceByInterval = hasDateInterval ? Frequence.SPECIFIC : Frequence.SINGLE;

  return {
    frequence: isVehicleMultiDayRange ? Frequence.SINGLE : frequenceByInterval,
    specificDays: isVehicleMultiDayRange ? undefined : specificDays,
    weekdays: hasDateInterval ? recurrenceWeekdaysAllTrue : defaultRecurrenceWeekdays
  };
}

export function ScheduleResourceBookDialog({
  calendarPrefetch,
  initialPageRange,
  onClose,
  open,
  row
}: Readonly<P>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const store = useStore<RootState>();
  const {
    userInformation: { sub, firstName, surname, email },
    settings
  } = useSelector((state: RootState) => state.login);

  const { mutateAsync: fetchSchedulesForRange, isLoading: isScheduleLoading } =
    useRemoteResourceScheduleByBookingObjectMutation();

  const [activeStep, setActiveStep] = useState(0);
  const [conferenceInviteStepCanProceed, setConferenceInviteStepCanProceed] = useState(true);
  const [conferencePrefetchMounted, setConferencePrefetchMounted] = useState(false);
  const [dateStepNextLoading, setDateStepNextLoading] = useState(false);
  const [bookingFrom, setBookingFrom] = useState("");
  const [bookingTo, setBookingTo] = useState("");
  const [chosenTimes, setChosenTimes] = useState({ endTime: "17:00", startTime: "09:00" });
  const [schedules, setSchedules] = useState<BookingScheduleInterface[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [bookingLeadTime, setBookingLeadTime] = useState<number | null>(null);

  const hydrateKeyRef = useRef<string>("");
  const vehicleInfoFetchKeyRef = useRef<string>("");
  const vehicleInfoFetchPromiseRef = useRef<Promise<BookingVehicleInfo> | null>(null);
  const prefetchedVehicleInfoRef = useRef<BookingVehicleInfo | null>(null);
  const resourceInfoFetchKeyRef = useRef<string>("");
  const resourceInfoFetchPromiseRef = useRef<Promise<BookingResourceInfo> | null>(null);
  const prefetchedResourceInfoRef = useRef<BookingResourceInfo | null>(null);
  const rowRef = useRef(row);
  rowRef.current = row;

  const hasConferenceStep = row?.bookingType === BookingType.CONFERENCEZONE;
  const confirmStepIndex = hasConferenceStep ? 2 : 1;

  useLayoutEffect(() => {
    if (!open) {
      setConferenceInviteStepCanProceed(true);
      return;
    }
    setConferenceInviteStepCanProceed(true);
  }, [open, row?.bookingInventoryId]);

  const resetLocalState = useCallback(() => {
    const {
      bookingEnd,
      bookingFrom: persistedBookingFrom,
      bookingStart
    } = store.getState().booking.inputs;
    setActiveStep(0);
    setConferenceInviteStepCanProceed(true);
    const r = defaultMonthRange();
    setBookingFrom(r.start);
    setBookingTo(r.end);
    const resetChosenTimesOpts = {
      bookingFromIso: r.start,
      bookingType: rowRef.current?.bookingType,
      persistedBookingEnd: bookingEnd,
      persistedBookingFrom,
      persistedBookingStart: bookingStart,
      standardEndTime: settings.standardEndTime ?? "17:00",
      standardStartTime: settings.standardStartTime ?? "09:00",
      timezone: settings.timezone
    };
    const chosenTimesAfterReset = initialChosenTimesForResourceBookDialog(resetChosenTimesOpts);
    setChosenTimes(chosenTimesAfterReset);
    setSchedules([]);
    setLoadError(false);
    setBookingLeadTime(null);
    setConferencePrefetchMounted(false);
    setDateStepNextLoading(false);
    hydrateKeyRef.current = "";
    vehicleInfoFetchKeyRef.current = "";
    vehicleInfoFetchPromiseRef.current = null;
    prefetchedVehicleInfoRef.current = null;
    resourceInfoFetchKeyRef.current = "";
    resourceInfoFetchPromiseRef.current = null;
    prefetchedResourceInfoRef.current = null;
  }, [settings.standardEndTime, settings.standardStartTime, settings.timezone, store]);

  const ensureVehicleInfoPrefetched = useCallback(async (): Promise<BookingVehicleInfo | null> => {
    const selectedRow = rowRef.current;
    const key =
      selectedRow?.bookingType === BookingType.VEHICLE
        ? `${selectedRow.bookingInventoryId}-${bookingFrom}-${bookingTo}`
        : "";
    const cached = tryReadVehicleInfoPrefetchCache(
      selectedRow,
      key,
      vehicleInfoFetchKeyRef.current,
      prefetchedVehicleInfoRef.current,
      vehicleInfoFetchPromiseRef.current
    );
    if (cached === null) {
      return null;
    }
    if (cached !== undefined) {
      return cached;
    }
    if (!selectedRow || selectedRow.bookingType !== BookingType.VEHICLE) {
      return null;
    }

    vehicleInfoFetchKeyRef.current = key;
    const fallbackVehicleInfo = createStubFleetVehicleForResourceBooking(selectedRow);
    const fetchPromise = getFleetVehicleInfo({
      vehicleInventoryId: selectedRow.bookingInventoryId,
      startDate: bookingFrom,
      endDate: bookingTo
    })
      .then(response => mapVehicleInventoryToFleetManagementRow(response.data))
      .catch(() => fallbackVehicleInfo)
      .then(vehicleInfo => {
        prefetchedVehicleInfoRef.current = vehicleInfo;
        return vehicleInfo;
      })
      .finally(() => {
        if (vehicleInfoFetchKeyRef.current === key) {
          vehicleInfoFetchPromiseRef.current = null;
        }
      });
    vehicleInfoFetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [bookingFrom, bookingTo]);

  useEffect(() => {
    if (!open || row?.bookingType !== BookingType.VEHICLE) return;
    ensureVehicleInfoPrefetched();
  }, [ensureVehicleInfoPrefetched, open, row?.bookingInventoryId, row?.bookingType]);

  const ensureResourceInfoPrefetched =
    useCallback(async (): Promise<BookingResourceInfo | null> => {
      const selectedRow = rowRef.current;
      if (selectedRow?.bookingType !== BookingType.RESOURCE) {
        return null;
      }

      const key = `${selectedRow.bookingInventoryId}-${selectedRow.resourceKind ?? "equipment"}`;
      if (resourceInfoFetchKeyRef.current === key && prefetchedResourceInfoRef.current) {
        setBookingLeadTime(prefetchedResourceInfoRef.current.bookingLeadTime);
        return prefetchedResourceInfoRef.current;
      }

      if (resourceInfoFetchKeyRef.current === key && resourceInfoFetchPromiseRef.current) {
        return resourceInfoFetchPromiseRef.current;
      }

      resourceInfoFetchKeyRef.current = key;
      const fetchPromise = fetchResourceInfoForConfirm(selectedRow)
        .then(resourceInfo => {
          prefetchedResourceInfoRef.current = resourceInfo;
          setBookingLeadTime(resourceInfo.bookingLeadTime);
          return resourceInfo;
        })
        .finally(() => {
          if (resourceInfoFetchKeyRef.current === key) {
            resourceInfoFetchPromiseRef.current = null;
          }
        });
      resourceInfoFetchPromiseRef.current = fetchPromise;
      return fetchPromise;
    }, []);

  useEffect(() => {
    if (!open || row?.bookingType !== BookingType.RESOURCE) return;
    ensureResourceInfoPrefetched();
  }, [ensureResourceInfoPrefetched, open, row?.bookingInventoryId, row?.bookingType]);

  const hydrateVehicleConfirmState = useCallback(
    (selectedRow: BookableBookingObjectDto, vehicleInfo: BookingVehicleInfo) => {
      const recurrenceState = deriveResourceRecurrenceState(
        bookingFrom,
        bookingTo,
        selectedRow.bookingType
      );
      dispatch(clearFloors());
      dispatch(clearInputs());
      dispatch(
        selectVehicleBooking({
          usersBookedFor: {
            firstName,
            surname,
            email,
            userId: sub,
            isExternal: false,
            bookingInventoryId: selectedRow.bookingInventoryId
          },
          vehicleInfo
        })
      );
      dispatch(
        setInputs({
          mode: BookingType.VEHICLE,
          bookingType: BookingType.VEHICLE,
          bookingFrom,
          bookingTo,
          bookingEnd: chosenTimes.endTime,
          bookingStart: chosenTimes.startTime,
          frequence: recurrenceState.frequence,
          specificDays: recurrenceState.specificDays,
          timezone: settings.timezone,
          isUserBookedBy: true,
          userBookedById: sub,
          userBookedBy: email,
          isExternal: false,
          weekdays: recurrenceState.weekdays
        })
      );
    },
    [
      bookingFrom,
      bookingTo,
      chosenTimes.endTime,
      chosenTimes.startTime,
      dispatch,
      email,
      firstName,
      settings.timezone,
      sub,
      surname
    ]
  );

  const hydrateResourceConfirmState = useCallback(
    (selectedRow: BookableBookingObjectDto, resourceInfo: BookingResourceInfo) => {
      const recurrenceState = deriveResourceRecurrenceState(
        bookingFrom,
        bookingTo,
        selectedRow.bookingType
      );
      dispatch(clearFloors());
      dispatch(clearInputs());
      dispatch(
        selectResourceBooking({
          usersBookedFor: {
            firstName,
            surname,
            email,
            userId: sub,
            isExternal: false,
            bookingInventoryId: selectedRow.bookingInventoryId
          },
          resourceInfo,
          resourceKind: resourceInfo.resourceKind,
          locationInventoryId: selectedRow.locationInventoryId,
          locationName: resourceInfo.locationName ?? selectedRow.locationName
        })
      );
      dispatch(
        setInputs({
          mode: BookingType.RESOURCE,
          bookingType: BookingType.RESOURCE,
          bookingFrom,
          bookingTo,
          bookingEnd: chosenTimes.endTime,
          bookingStart: chosenTimes.startTime,
          frequence: recurrenceState.frequence,
          specificDays: recurrenceState.specificDays,
          timezone: settings.timezone,
          isUserBookedBy: true,
          userBookedById: sub,
          userBookedBy: email,
          isExternal: false,
          weekdays: recurrenceState.weekdays
        })
      );
    },
    [
      bookingFrom,
      bookingTo,
      chosenTimes.endTime,
      chosenTimes.startTime,
      dispatch,
      email,
      firstName,
      settings.timezone,
      sub,
      surname
    ]
  );

  useEffect(() => {
    if (!open) {
      resetLocalState();
      return;
    }
    if (!row) return;

    const initial =
      initialPageRange?.start && initialPageRange?.end ? initialPageRange : defaultMonthRange();
    setBookingFrom(initial.start);
    setBookingTo(initial.end);
    const {
      bookingEnd,
      bookingFrom: persistedBookingFrom,
      bookingStart
    } = store.getState().booking.inputs;
    const openChosenTimesOpts = {
      bookingFromIso: initial.start,
      bookingType: row.bookingType,
      persistedBookingEnd: bookingEnd,
      persistedBookingFrom,
      persistedBookingStart: bookingStart,
      standardEndTime: settings.standardEndTime ?? "17:00",
      standardStartTime: settings.standardStartTime ?? "09:00",
      timezone: settings.timezone
    };
    const chosenTimesForOpen = initialChosenTimesForResourceBookDialog(openChosenTimesOpts);
    setChosenTimes(chosenTimesForOpen);
    setActiveStep(0);
  }, [
    open,
    row,
    initialPageRange,
    resetLocalState,
    settings.standardEndTime,
    settings.standardStartTime,
    settings.timezone,
    store
  ]);

  useEffect(() => {
    if (open) return;
    setConferencePrefetchMounted(false);
    setDateStepNextLoading(false);
  }, [open]);

  useEffect(() => {
    if (!open || !row) return;
    if (row.bookingType !== BookingType.CONFERENCEZONE) return;
    const { description, subject, zoneAccess } = store.getState().booking.inputs;
    dispatch(
      setInputs({
        description: description?.trim() ? description : "",
        subject: subject?.trim() ? subject : "",
        zoneAccess: zoneAccess?.length ? [...zoneAccess] : []
      })
    );
  }, [dispatch, open, row?.bookingInventoryId, row?.bookingType, store]);

  useEffect(() => {
    if (open) return;
    const r = rowRef.current;
    if (r?.bookingType === BookingType.CONFERENCEZONE) {
      dispatch(
        setInputs({
          description: undefined,
          onlineMeetingProvider: undefined,
          zoneAccess: []
        })
      );
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (!open) return;
    const r = rowRef.current;
    if (!r) return;

    let cancelled = false;
    setLoadError(false);

    const tz = settings.timezone;
    const pf = calendarPrefetch;

    if (
      pf?.schedules &&
      pf.rangeStartIso &&
      pf.rangeEndIso &&
      isDialogDateRangeWithinCalendarPrefetchRange(
        bookingFrom,
        bookingTo,
        pf.rangeStartIso,
        pf.rangeEndIso,
        tz
      )
    ) {
      const filtered = selectSchedulesOverlappingDialogDateRange(
        pf.schedules,
        bookingFrom,
        bookingTo,
        tz
      );
      if (!cancelled) {
        setSchedules(filtered);
      }
      return () => {
        cancelled = true;
      };
    }

    fetchSchedulesForRange(
      r.bookingType === BookingType.RESOURCE
        ? buildResourceCalendarByObjectRequest(r, bookingFrom, bookingTo)
        : {
            bookingInventoryId: r.bookingInventoryId,
            bookingType: r.bookingType,
            endDate: bookingTo,
            startDate: bookingFrom
          }
    )
      .then(data => {
        if (!cancelled) setSchedules(data);
      })
      .catch(() => {
        if (!cancelled) {
          setSchedules([]);
          setLoadError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    bookingFrom,
    bookingTo,
    calendarPrefetch?.rangeEndIso,
    calendarPrefetch?.rangeStartIso,
    calendarPrefetch?.schedules,
    fetchSchedulesForRange,
    open,
    row?.bookingInventoryId,
    row?.bookingType,
    settings.timezone
  ]);

  const runHydrateConfirm = useCallback(async () => {
    if (!row) return;

    const conferenceSnapshot =
      row.bookingType === BookingType.CONFERENCEZONE
        ? {
            conferenceService: store.getState().booking.inputs.conferenceService,
            description: store.getState().booking.inputs.description,
            onlineMeetingProvider: store.getState().booking.inputs.onlineMeetingProvider,
            subject: store.getState().booking.inputs.subject,
            zoneAccess: store.getState().booking.inputs.zoneAccess
          }
        : null;

    if (row.bookingType === BookingType.VEHICLE) {
      const bookingInputs = store.getState().booking.inputs;
      const alreadyHydratedForConfirmStep =
        bookingInputs.mode === BookingType.VEHICLE &&
        bookingInputs.bookingFrom === bookingFrom &&
        bookingInputs.bookingTo === bookingTo &&
        bookingInputs.bookingStart === chosenTimes.startTime &&
        bookingInputs.bookingEnd === chosenTimes.endTime &&
        bookingInputs.bookedVehicle?.id === row.bookingInventoryId;
      if (alreadyHydratedForConfirmStep) {
        return;
      }

      const vehicleInfo = vehicleInfoFromPrefetchOrStub(await ensureVehicleInfoPrefetched(), row);
      hydrateVehicleConfirmState(row, vehicleInfo);
      return;
    }

    if (row.bookingType === BookingType.RESOURCE) {
      const bookingInputs = store.getState().booking.inputs;
      const alreadyHydratedForConfirmStep =
        bookingInputs.mode === BookingType.RESOURCE &&
        bookingInputs.bookingFrom === bookingFrom &&
        bookingInputs.bookingTo === bookingTo &&
        bookingInputs.bookingStart === chosenTimes.startTime &&
        bookingInputs.bookingEnd === chosenTimes.endTime &&
        bookingInputs.bookedResource?.id === row.bookingInventoryId;
      if (alreadyHydratedForConfirmStep) {
        return;
      }

      const resourceInfo = resourceInfoFromPrefetchOrStub(
        await ensureResourceInfoPrefetched(),
        row
      );
      hydrateResourceConfirmState(row, resourceInfo);
      return;
    }

    dispatch(clearFloors());
    dispatch(clearInputs());

    const { floorInventoryId, locationInventoryId } = resolveResourceBookLocationForConfirm(
      schedules,
      row
    );

    const subjectValue =
      row.bookingType === BookingType.CONFERENCEZONE
        ? conferenceSnapshot?.subject?.trim() || undefined
        : undefined;
    const recurrenceState = deriveResourceRecurrenceState(bookingFrom, bookingTo, row.bookingType);

    dispatch(
      setInputs({
        mode: row.bookingType,
        bookingType: getBookingType(row.bookingType),
        usersBookedFor: [
          {
            userId: sub,
            firstName,
            surname,
            email,
            isExternal: false,
            bookingInventoryId: row.bookingInventoryId
          }
        ],
        bookingFrom,
        bookingTo,
        bookingEnd: chosenTimes.endTime,
        bookingStart: chosenTimes.startTime,
        frequence: recurrenceState.frequence,
        specificDays: recurrenceState.specificDays,
        isUserBookedBy: true,
        automatedSeating: false,
        timezone: settings.timezone,
        selectedLocation: locationInventoryId,
        locationName: row.locationName,
        subject: subjectValue,
        description:
          row.bookingType === BookingType.CONFERENCEZONE
            ? conferenceSnapshot?.description || undefined
            : undefined,
        zoneAccess:
          row.bookingType === BookingType.CONFERENCEZONE
            ? conferenceSnapshot?.zoneAccess ?? null
            : null,
        onlineMeetingProvider:
          row.bookingType === BookingType.CONFERENCEZONE
            ? conferenceSnapshot?.onlineMeetingProvider ?? null
            : null,
        conferenceService:
          row.bookingType === BookingType.CONFERENCEZONE
            ? conferenceSnapshot?.conferenceService
            : undefined,
        userBookedById: sub,
        userBookedBy: email,
        isExternal: false,
        weekdays: recurrenceState.weekdays
      })
    );

    if (!locationInventoryId) return;

    const weekdays = store.getState().booking.inputs.weekdays;

    await dispatch(
      fetchBookingFloors({
        bookingType: getBookingType(row.bookingType),
        description: conferenceSnapshot?.description ?? "",
        endDate: bookingTo,
        floorInventoryId,
        locationInventoryId,
        startDate: bookingFrom,
        endTime: chosenTimes.endTime ?? undefined,
        startTime: chosenTimes.startTime ?? undefined,
        users: store.getState().booking.inputs.usersBookedFor,
        weekdays
      })
    ).unwrap();

    const floorInventory = store.getState().booking.floors.inventory;
    if (floorInventory.length > 0) {
      dispatch(
        setFloors({
          currentFloorIndex: resolveCurrentFloorIndexForResourceBookConfirm(
            floorInventory,
            floorInventoryId
          )
        })
      );
    }
  }, [
    bookingFrom,
    bookingTo,
    chosenTimes.endTime,
    chosenTimes.startTime,
    dispatch,
    email,
    ensureVehicleInfoPrefetched,
    ensureResourceInfoPrefetched,
    firstName,
    hydrateResourceConfirmState,
    hydrateVehicleConfirmState,
    row,
    schedules,
    settings.timezone,
    store,
    sub,
    surname
  ]);

  useEffect(() => {
    if (!open || !row) return;
    if (activeStep !== confirmStepIndex) return;

    const bi = store.getState().booking.inputs;
    const conferenceServiceOrder =
      row.bookingType === BookingType.CONFERENCEZONE
        ? bi.conferenceService?.[row.bookingInventoryId]
        : undefined;
    const conferenceKey =
      row.bookingType === BookingType.CONFERENCEZONE
        ? `${bi.subject ?? ""}|${bi.description ?? ""}|${(bi.zoneAccess ?? []).length}|${JSON.stringify(conferenceServiceOrder ?? null)}`
        : "";
    const key = `${row.bookingInventoryId}-${bookingFrom}-${bookingTo}-${chosenTimes.startTime}-${chosenTimes.endTime}-${activeStep}-${conferenceKey}`;
    if (hydrateKeyRef.current === key) return;
    hydrateKeyRef.current = key;

    runHydrateConfirm().catch(() => {
      enqueueSnackbar(t("_scheduleResourceBook_hydrateError"), { variant: "error" });
    });
  }, [
    activeStep,
    bookingFrom,
    bookingTo,
    chosenTimes.endTime,
    chosenTimes.startTime,
    confirmStepIndex,
    enqueueSnackbar,
    open,
    row,
    runHydrateConfirm,
    store,
    t
  ]);

  const handleClose = () => {
    onClose();
  };

  const handleConferenceServicesPrefetchReady = useCallback(() => {
    setDateStepNextLoading(false);
    setConferencePrefetchMounted(false);
    setActiveStep(1);
  }, []);

  const handleBack = () => {
    setDateStepNextLoading(false);
    setConferencePrefetchMounted(false);
    setActiveStep(prev => prev - 1);
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const days = listIsoDaysInclusive(bookingFrom, bookingTo);
      if (days.length === 0) {
        enqueueSnackbar(t("_scheduleResourceBook_invalidDateRange"), { variant: "warning" });
        return;
      }
      const leadTimeBlocksStartDay =
        resourceBookTimePickerValidRanges({
          bookingFromIso: bookingFrom,
          bookingLeadTimeHours: bookingLeadTime,
          timezone: settings.timezone
        }).length === 0;
      if (leadTimeBlocksStartDay) {
        enqueueSnackbar(t("_scheduleResourceBook_leadTimeBlocksStartDay"), { variant: "warning" });
        return;
      }
      const startDt = DateTime.fromISO(`${bookingFrom}T${chosenTimes.startTime}`);
      const endDt = DateTime.fromISO(`${bookingTo}T${chosenTimes.endTime}`);
      /** istanbul ignore if */
      if (!startDt.isValid || !endDt.isValid || endDt <= startDt) {
        enqueueSnackbar(t("_scheduleResourceBook_timesInvalid"), { variant: "warning" });
        return;
      }
      if (hasConferenceStep) {
        const recurrenceState = deriveResourceRecurrenceState(
          bookingFrom,
          bookingTo,
          row?.bookingType
        );
        queryClient.invalidateQueries(["booking-service-max-set-up-time"]);
        dispatch(
          setInputs({
            bookingFrom,
            bookingTo,
            bookingEnd: chosenTimes.endTime,
            bookingStart: chosenTimes.startTime,
            frequence: recurrenceState.frequence,
            specificDays: recurrenceState.specificDays,
            timezone: settings.timezone,
            weekdays: recurrenceState.weekdays
          })
        );
        setConferencePrefetchMounted(true);
        setDateStepNextLoading(true);
        return;
      }
      if (row?.bookingType === BookingType.VEHICLE) {
        const vehicleInfo = vehicleInfoFromPrefetchOrStub(await ensureVehicleInfoPrefetched(), row);
        hydrateVehicleConfirmState(row, vehicleInfo);
      }
      if (row?.bookingType === BookingType.RESOURCE) {
        const resourceInfo = resourceInfoFromPrefetchOrStub(
          await ensureResourceInfoPrefetched(),
          row
        );
        hydrateResourceConfirmState(row, resourceInfo);
      }
      setActiveStep(confirmStepIndex);
      return;
    }

    if (activeStep === 1 && hasConferenceStep) {
      if (!conferenceInviteStepCanProceed) {
        enqueueSnackbar(t("_scheduleResourceBook_mandatoryServicesIncomplete"), {
          variant: "warning"
        });
        return;
      }
      setActiveStep(confirmStepIndex);
    }
  };

  const steps = useMemo(() => {
    const base = [{ key: "date", label: t("_scheduleResourceBook_stepDate") }];
    if (hasConferenceStep) {
      base.push({ key: "conference", label: t("Invite") });
    }
    base.push({ key: "confirm", label: t("Confirm") });
    return base;
  }, [hasConferenceStep, t]);

  /** istanbul ignore next */
  const errorToState = useCallback(
    (error: string) => {
      dispatch(setInputs({ errorConfirm: error }));
    },
    [dispatch]
  );

  if (!row) return null;

  const isDateStepNextLoading = activeStep === 0 && dateStepNextLoading;
  const isConferenceDateStepNextLoading = isDateStepNextLoading && hasConferenceStep;
  const isDateStepNextDisabled = isDateStepNextLoading;

  return (
    <Dialog fullWidth maxWidth="xl" onClose={handleClose} open={open}>
      <DialogTitle>
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
          <Typography component="span" variant="h6">
            {t("_scheduleResourceBook_dialogTitle", { name: row.objectName })}
          </Typography>
          <IconButton aria-label={t("Close")} onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ overflowX: "hidden", position: "relative" }}>
        <Stepper
          activeStep={activeStep}
          data-testid="schedule-resource-book-stepper"
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: 2,
            display: { xs: "none", md: "flex" },
            mb: 2,
            padding: 2
          }}
        >
          {steps.map(step => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
          <MobileStepper
            activeStep={activeStep}
            backButton={
              <Button
                sx={{ minWidth: 40, minHeight: 24 }}
                color="inherit"
                disabled={activeStep === 0}
                onClick={handleBack}
                size="small"
              >
                <KeyboardArrowLeft />
              </Button>
            }
            data-testid="schedule-resource-book-mobile-stepper"
            nextButton={
              activeStep < confirmStepIndex ? (
                <Button
                  sx={{ minWidth: 40, minHeight: 24 }}
                  aria-busy={isConferenceDateStepNextLoading}
                  color="primary"
                  disabled={isDateStepNextDisabled}
                  onClick={handleNext}
                  size="small"
                >
                  {isConferenceDateStepNextLoading ? (
                    <CircularProgress
                      color="inherit"
                      data-testid="schedule-resource-book-next-loading"
                      size={20}
                    />
                  ) : (
                    <KeyboardArrowRight />
                  )}
                </Button>
              ) : (
                <Box sx={{ minWidth: 80 }} />
              )
            }
            position="static"
            steps={steps.length}
            sx={{
              backgroundColor: theme.palette.background.default,
              borderRadius: 2,
              pb: 1.75,
              pl: 1,
              pr: 1,
              pt: 1.5
            }}
            variant="dots"
          />
        </Box>

        <Box
          sx={{
            alignItems: "center",
            columnGap: 1,
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "1fr auto 1fr",
            mb: 2,
            px: 0.5
          }}
        >
          <Box sx={{ justifySelf: "start" }}>
            <Button
              data-testid="schedule-resource-book-back-button"
              color="secondary"
              disabled={activeStep === 0}
              onClick={handleBack}
              size="small"
              startIcon={<KeyboardArrowLeft />}
            >
              {t("Back")}
            </Button>
          </Box>
          <Box
            aria-hidden
            data-testid="schedule-resource-book-desktop-step-dots"
            sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center" }}
          >
            {steps.map((step, index) => (
              <Box
                key={step.key}
                sx={{
                  bgcolor:
                    index === activeStep
                      ? theme.palette.primary.main
                      : theme.palette.action.disabledBackground,
                  borderRadius: "50%",
                  height: 8,
                  opacity: index === activeStep ? 1 : 0.55,
                  width: 8
                }}
              />
            ))}
          </Box>
          <Box sx={{ justifySelf: "end", minHeight: 36 }}>
            <Button
              data-testid="schedule-resource-book-next-button"
              aria-busy={isConferenceDateStepNextLoading}
              color="primary"
              disabled={activeStep == confirmStepIndex || isDateStepNextDisabled}
              endIcon={isConferenceDateStepNextLoading ? undefined : <KeyboardArrowRight />}
              onClick={handleNext}
              size="small"
            >
              {isConferenceDateStepNextLoading ? (
                <CircularProgress
                  color="inherit"
                  data-testid="schedule-resource-book-next-loading"
                  size={20}
                />
              ) : (
                t("Next")
              )}
            </Button>
          </Box>
        </Box>

        {activeStep === 0 && (
          <ScheduleResourceBookDateStep
            bookingFrom={bookingFrom}
            bookingTo={bookingTo}
            bookingLeadTime={bookingLeadTime}
            chosenTimes={chosenTimes}
            isFetching={isScheduleLoading}
            loadError={loadError}
            onWideRangeOccupancyDaySelected={dayIso => /* istanbul ignore next */ {
              const recurrenceState = deriveResourceRecurrenceState(
                dayIso,
                dayIso,
                row?.bookingType
              );
              setBookingFrom(dayIso);
              setBookingTo(dayIso);
              dispatch(
                setInputs({
                  bookingFrom: dayIso,
                  bookingTo: dayIso,
                  frequence: recurrenceState.frequence,
                  specificDays: recurrenceState.specificDays,
                  weekdays: recurrenceState.weekdays
                })
              );
            }}
            schedules={schedules}
            setChosenTimes={setChosenTimes}
            timezone={settings.timezone}
          />
        )}

        {open && hasConferenceStep && (activeStep === 1 || conferencePrefetchMounted) && (
          <Box
            aria-hidden={conferencePrefetchMounted && activeStep === 0}
            sx={
              conferencePrefetchMounted && activeStep === 0
                ? {
                    contain: "strict",
                    height: 0,
                    left: 0,
                    overflow: "hidden",
                    pointerEvents: "none",
                    position: "fixed",
                    top: 0,
                    visibility: "hidden",
                    width: 0
                  }
                : undefined
            }
          >
            <ScheduleResourceBookConferenceStep
              bookingFrom={bookingFrom}
              bookingTo={bookingTo}
              chosenTimes={chosenTimes}
              dialogOpen={open}
              onConferenceInviteStepValidityChange={setConferenceInviteStepCanProceed}
              onConferenceServicesPrefetchReady={handleConferenceServicesPrefetchReady}
              prefetchMode={conferencePrefetchMounted && activeStep === 0}
              row={row}
            />
          </Box>
        )}

        {activeStep === confirmStepIndex && (
          <Box>
            <FormConfirm
              errorToState={errorToState}
              row={row}
              isResourceBooking={true}
              onResourceBookingClose={onClose}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
